import type { EChartsOption, ECharts } from 'echarts';
import { ECHARTS_TOOLTIP_STYLE, escapeHtml } from '../components/CustomDashboard/WidgetRenderer/echartsUtils';

/**
 * Shared option builder for the donut charts that replaced the google-charts
 * PieChart (platform distribution, breakdown pies). Keeps sizing, labels and
 * tooltips identical across all of them.
 */

export type DonutLegendPosition = 'labeled' | 'right' | 'left' | 'top' | 'bottom' | 'none';

export interface DonutDatum {
  /** Display label. */
  name: string;
  value: number;
  /** Pre-formatted count (e.g. "1.2K") shown in labels, legend and tooltip. */
  formattedCount: string;
  /** Per-slice color override; falls back to the palette order. */
  color?: string;
}

export interface DonutOptionParams {
  data: DonutDatum[];
  colors: string[];
  /** 'labeled' draws "Name: count" callouts by each slice instead of a legend. */
  legendPosition?: DonutLegendPosition;
  labelFontSize?: number;
  labelColor?: string;
  isClickable?: boolean;
  seriesName?: string;
  /** Returns the live chart width; needed to extend label lines under labels. */
  getChartWidth?: () => number;
}

// echarts SVG text cannot read CSS variables, so App.scss tokens are mirrored here.
const DEFAULT_TEXT_COLOR = '#2a324b'; // --clr-gray600
const LABEL_VALUE_COLOR = '#767b91'; // --clr-gray400

export const buildDonutOption = ({
  data,
  colors,
  legendPosition = 'labeled',
  labelFontSize = 14,
  labelColor = DEFAULT_TEXT_COLOR,
  isClickable = true,
  seriesName,
  getChartWidth,
}: DonutOptionParams): EChartsOption => {
  const showSliceLabels = legendPosition === 'labeled';
  const showLegend = !showSliceLabels && legendPosition !== 'none';

  const legend: EChartsOption['legend'] = {
    show: showLegend,
    type: 'scroll',
    icon: 'circle',
    // Match the old legend spacing: 14px dot, 5px to the text, 18px between items.
    itemWidth: 14,
    itemHeight: 14,
    itemGap: 18,
    padding: [4, 8],
    // Legend clicks open the slice's content modal (see `getDonutClickIndex`)
    // instead of toggling slices. `selectedMode: false` would make the items
    // silent, so clicks are kept enabled and the toggle is reverted on click.
    triggerEvent: true,
    textStyle: { fontSize: labelFontSize, color: labelColor },
    formatter: (name: string) => {
      const item = data.find((d) => d.name === name);
      return item ? `${name}: ${item.formattedCount}` : name;
    },
  };
  if (legendPosition === 'top') legend.top = 0;
  else if (legendPosition === 'bottom') legend.bottom = 12;
  else if (legendPosition === 'left') { legend.left = 0; legend.orient = 'vertical'; legend.top = 'middle'; }
  else if (legendPosition === 'right') { legend.right = 0; legend.orient = 'vertical'; legend.top = 'middle'; }

  const center: [string, string] =
    legendPosition === 'top' ? ['50%', '54%']
    : legendPosition === 'bottom' ? ['50%', '46%']
    : ['50%', '50%'];

  const seriesData = data.map((item) =>
    item.color ? { ...item, itemStyle: { color: item.color } } : item,
  );

  return {
    color: colors,
    aria: { enabled: true },
    tooltip: {
      trigger: 'item',
      appendToBody: true,
      ...ECHARTS_TOOLTIP_STYLE,
      formatter: (params: any) =>
        `${params.marker}${escapeHtml(params.name)}: <b>${escapeHtml(params.data?.formattedCount)}</b> (${params.percent}%)`,
    },
    legend,
    series: [
      {
        name: seriesName,
        type: 'pie',
        // Matches the previous google-charts sizing with a 0.65 hole ratio
        // (inner = 0.65 * outer): ~85% of the box with slice callouts, ~80%
        // with a legend row above/below.
        radius: showSliceLabels ? ['55%', '85%'] : ['52%', '80%'],
        center,
        data: seriesData,
        percentPrecision: 1,
        // Hairline slices stay visible/hoverable but, like the old chart, get
        // no callout so tiny categories don't stack labels at the top.
        minAngle: 0.5,
        minShowLabelAngle: 1,
        avoidLabelOverlap: true,
        cursor: isClickable ? 'pointer' : 'default',
        itemStyle: { borderColor: 'transparent', borderWidth: 0 },
        label: {
          show: showSliceLabels,
          position: 'outside',
          // Labels align to the chart edge (name over value) with the leader
          // line extended under the text. Callers cap the chart box width so
          // these lines stay short in wide cards.
          alignTo: 'edge',
          edgeDistance: 10,
          minMargin: 5,
          lineHeight: 16,
          fontSize: labelFontSize,
          color: labelColor,
          formatter: (params: any) =>
            `{name|${params.name}: ${params.data?.formattedCount ?? ''}}\n{value|${params.percent}%}`,
          rich: {
            name: { fontSize: labelFontSize, lineHeight: 18, color: labelColor },
            value: { fontSize: Math.max(labelFontSize - 1, 11), lineHeight: 17, color: LABEL_VALUE_COLOR },
          },
        },
        labelLine: {
          show: showSliceLabels,
          length: 15,
          length2: 0,
          maxSurfaceAngle: 80,
          lineStyle: { width: 1 },
        },
        labelLayout: showSliceLabels
          ? (params: any) => {
              const chartWidth = getChartWidth?.() ?? 0;
              const points = params.labelLinePoints;
              if (!points || !params.labelRect || !chartWidth) return {};
              const isLeft = params.labelRect.x + params.labelRect.width / 2 < chartWidth / 2;
              // Extend the last segment under the label to its far edge.
              points[2][0] = isLeft
                ? params.labelRect.x
                : params.labelRect.x + params.labelRect.width;
              return { labelLinePoints: points };
            }
          : undefined,
        emphasis: {
          scale: isClickable,
          scaleSize: 4,
          label: { show: showSliceLabels },
        },
      },
    ],
  };
};

/**
 * Resolves the data index behind an echarts click on a donut, whether the
 * click landed on a slice or on its legend entry. A legend click also reverts
 * the default show/hide toggle so the slice stays visible.
 */
export const getDonutClickIndex = (params: any, chart?: ECharts | null): number | null => {
  if (params?.componentType === 'legend') {
    const name = params.value ?? params.name;
    if (chart && typeof name === 'string') {
      chart.dispatchAction({ type: 'legendSelect', name });
    }
    return typeof params.dataIndex === 'number' ? params.dataIndex : null;
  }
  if (params?.componentType === 'series' && typeof params.dataIndex === 'number') {
    return params.dataIndex;
  }
  return null;
};
